// @ts-strict-ignore
import { SelectionModel } from '@project/components/article/model/selection.model';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { ProjectModel } from '@project/model/project.model';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { Dictionary } from 'typescript-collections';
import { Matrix4, Vector3 } from 'three';
import { ArticleUtils } from '@article/util/article-utils';
import { ProjectHelper } from '@project/helper/project.helper';
import { ReplaceReferenceArticleLocationVO } from '@article/model/vo/replace-reference-article-location.vo';
import { CustomArticleVO } from '@project/components/article/model/vo/custom-article.vo';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { SelectionArticleGroupIdentity } from '@project/components/article/model/vo/selection-article-group-identity';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { AlternativeUsageConnectionData } from '../model/alternative-usage-connection-data';
import { UndoAttachArticlesOperation } from '@shared/undo/operation/undo-attach-articles-operation';
import { UndoAlternativeReplaceArticleOperation } from '@shared/undo/operation/undo-alternative-replace-article-operation';
import { IOperation } from '@shared/undo/operation/i-operation';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO, TextureAreaUsageVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { PropertyVO } from '../model/vo/property.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { PropertyUtils } from '@article/util/property-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { TextCheckSpacingData } from '@3d-scene/manager/text-check-spacing-data';
import { CloneUtils } from '@shared/util/clone-utils';
import { ConnectArticleHelper } from '@article/helper/connect-article.helper';
import { LabelingArticleRotationHelper } from '../../label/helper/labeling-article-rotation.helper';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { RotateArticlePresenter } from '../presenter/rotate-article.presenter';
import { ReplaceLabelingUtils } from '../../label/replace-labeling-helper/replace-labeling-utils';
import { ArticleCreationManager } from './article-creation.manager';
import { SuitableConnectionsVO } from '../model/vo/suitable-connections.vo';
import { ConnectionCubeSearchVector } from '@article/components/connections/connection-creation/search/connection-cube-search-vector';
import { DirectPrintingHelper } from '@article/helper/direct-printing/direct-printing.helper';
import { DefinedMetaElementInfoPropertyDef } from '../../../constants/defined-meta-element-info-property-def';
import { UserModel } from '@user/model/user.model';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { InsertArticleService } from '../service/insert-article.service';
import { InsertedArticleData } from '../processor/i-article-location.processor';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { Injectable } from '@angular/core';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { IsProjectType } from '../../../model/is-project-type';

export type TextureAreaInfo = {
  compositionRectangle: Rectangle3D;
  textValue: TextValueVO;
  imageUrl: string;
  imageHash: string;
  previousMerged: boolean;
  nextMerged: boolean;
};

@Injectable({ providedIn: 'root' })
export abstract class BaseReplaceArticleManager {
  private _selectReplacedArticle: boolean = true;
  protected isActive: boolean = false;

  protected article: ArticleVO;
  protected processedUsageId: string;
  protected selectedArticleIds: string[] = [];

  protected insertedArticleUsageIds: string[] = [];
  protected commonMoveArticlesMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
  protected detachArticlesOperation: UndoDetachArticlesOperation;

  protected _rotationMap: Dictionary<string, Matrix4> = new Dictionary<string, Matrix4>();
  protected _articleIdToAlternativeUsageConnectionDatas: Dictionary<string, AlternativeUsageConnectionData[]> = new Dictionary<
    string,
    AlternativeUsageConnectionData[]
  >();

  protected _creationManager: ArticleCreationManager;
  protected _accessoryReplaceEnabled: boolean = false;
  protected _suitableGivingConnections: SuitableConnectionsVO;
  protected _acceptingConnectionVectors: ConnectionCubeSearchVector[] = [];

  protected _articleIdToReplacedArticleId: Dictionary<string, string> = new Dictionary<string, string>();
  protected _replacedTextureAreasInfo: Dictionary<string, TextureAreaInfo[]> = new Dictionary<string, TextureAreaInfo[]>();

  constructor(
    protected userModel: UserModel,
    protected selectionModel: SelectionModel,
    protected selectionModelService: SelectionModelService,
    protected projectModel: ProjectModel,
    protected projectModelService: ProjectModelService,
    protected projectArticleService: ProjectArticleService,
    protected projectMetaElementService: ProjectMetaElementService,
    private projectHelper: ProjectHelper,
    private undoRedoHelper: UndoRedoHelper,
    private articleAccessoryManager: ArticleAccessoryManager,
    private directPrintingHelper: DirectPrintingHelper,
    protected containerService: ContainerService,
    protected undoRedoUtils: UndoRedoUtils,
    protected insertArticleUtils: InsertArticleUtils,
    private labelingArticleRotationHelper: LabelingArticleRotationHelper,
    protected replaceLabelingUtils: ReplaceLabelingUtils,
    protected compositeArticleUtils: CompositeArticleUtils,
    protected compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private rotateArticlePresenter: RotateArticlePresenter,
    private labelingUtils: LabelingUtils,
    protected articleContainerUtils: ArticleContainerUtils,
    protected _insertArticleService: InsertArticleService,
    protected detachArticleUsageService: DetachArticleUsageService,
    private articleConnectionService: ArticleConnectionService
  ) {}

  public async replaceSelectedAccessoryArticles(creationManager: ArticleCreationManager, suitableGivingConnections: SuitableConnectionsVO,
    acceptingConnectionVectors: ConnectionCubeSearchVector[]): Promise<void> {
    const accessoryContainers: ArticleContainer[] = [];
    let articleContainer: ArticleContainer;
    for (const usageId of this.selectionModel.getArticleUsageIds()) {
      articleContainer = this.containerService.findArticleContainerInGlobal(usageId);
      if (articleContainer) {
        accessoryContainers.push(articleContainer);
      }
    }

    const unremovedComplexPartArticles: ArticleContainer[] = [];
    const filteredChildContainers: ArticleContainer[] = this.getFilteredChildContainers(accessoryContainers, accessoryContainers,
      unremovedComplexPartArticles);
    await this.init(accessoryContainers, filteredChildContainers, this.article, unremovedComplexPartArticles);
    this._accessoryReplaceEnabled = true;
    this._suitableGivingConnections = suitableGivingConnections;
    this._acceptingConnectionVectors = acceptingConnectionVectors;
    this._creationManager = creationManager;

    await this.startProcessingAccessoryReplacement(accessoryContainers);
  }

  public async replaceSelectedArticles(article: ArticleVO | undefined): Promise<void> {
    await this.replaceArticlesByUsageIds(this.selectionModel.getArticleUsageIds(), article);
  }

  public async replaceArticlesByUsageIds(articleUsageIds: string[], article: ArticleVO | undefined): Promise<void> {
    if (this.isActive || !article) {
      return;
    }

    // filter and sort articles on rail, exclude articles which are part of complex article modification of which is not allowed
    // (in case not all parts are selected)
    const commonArticleContainersOnRail: ArticleContainer[] = this.commonFilterArticleContainersOnRail(articleUsageIds);
    // additionally filter parts of complex article which consists of multiple base parts (parts connected to rail)
    // leftmost part is only kept in the result (applied only to the case when modification of complex article is not allowed)
    const filteredArticleContainersOnRail: ArticleContainer[] = this.filterComplexArticlesOnRail(commonArticleContainersOnRail);
    // list articles which were excluded by previous filtering
    // "unremovedComplexPartArticles" - are basically articles which will not be tried to be re-connected or replaced
    // these parts will be just deleted at the end of replace-operation
    const unremovedComplexPartArticles: ArticleContainer[] = commonArticleContainersOnRail.concat();
    ArrayUtils.removeAllItemsInArray(unremovedComplexPartArticles, filteredArticleContainersOnRail);

    const filteredChildContainers: ArticleContainer[] = this.getFilteredChildContainers(
      commonArticleContainersOnRail,
      filteredArticleContainersOnRail,
      unremovedComplexPartArticles
    );

    // some article containers included in articleContainersOnRail can be connected to another article connected to mounting rail
    // (e.g. article connected to n-busbar)
    // so such articles should not be replaced, because they will be removed as other accessory articles
    for (let i = 0; i < filteredArticleContainersOnRail.length; i++) {
      const containerOnRail: ArticleContainer = filteredArticleContainersOnRail[i];
      if (this.articleContainerUtils.findArticleContainerByGUID(containerOnRail.articleGUID, filteredChildContainers)) {
        filteredArticleContainersOnRail.splice(i, 1);
        i--;
      }
    }
    await this.replaceFilteredArticles(filteredArticleContainersOnRail, filteredChildContainers, article, unremovedComplexPartArticles);
  }

  protected getFilteredChildContainers(
    commonArticleContainers: ArticleContainer[],
    filteredCommonArticles: ArticleContainer[],
    unremovedComplexPartArticles: ArticleContainer[]
  ): ArticleContainer[] {
    const childContainers: ArticleContainer[] = this.insertArticleUtils.findChildArticleContainers(commonArticleContainers, false, true);
    const labelingArticles: ArticleContainer[] = this.replaceLabelingUtils.getLabelStripsToRecreateByArticles(commonArticleContainers);
    ArrayUtils.mergeArrays(childContainers, labelingArticles);
    // filter connected articles, keep it in the result if:
    // - it's not part of complex article
    // - it belongs to the same complex article as given articles on rail
    // - it's part of complex article, but modification of this complex article is allowed
    const filteredChildContainers: ArticleContainer[] = this.filterComplexChildArticleContainers(filteredCommonArticles, childContainers);
    const unremovedComplexChildContainers: ArticleContainer[] = childContainers.concat();
    ArrayUtils.removeAllItemsInArray(unremovedComplexChildContainers, filteredChildContainers);
    ArrayUtils.mergeArrays(unremovedComplexPartArticles, unremovedComplexChildContainers);
    return filteredChildContainers;
  }

  protected async replaceFilteredArticles(articleContainersOnRail: ArticleContainer[], childContainers: ArticleContainer[], article: ArticleVO,
    unremovedComplexPartArticles: ArticleContainer[]): Promise<void> {
    await this.init(articleContainersOnRail, childContainers, article, unremovedComplexPartArticles);
    await this.startProcessingPreparedArticles();
  }

  protected abstract startProcessingAccessoryReplacement(accessoryContainers: ArticleContainer[]): Promise<void>;

  protected abstract startProcessingPreparedArticles(): Promise<void>;

  protected async init(articleContainersOnRail: ArticleContainer[], childContainers: ArticleContainer[], article: ArticleVO,
    unremovedComplexPartArticles: ArticleContainer[]): Promise<void> {
    this.clear();

    this.isActive = true;
    this.article = article;

    this.selectedArticleIds = this.selectionModel.getArticleUsageIds();

    const childContainersForDelete: ArticleContainer[] = this.getChildContainersForDelete(childContainers);
    const processedContainers: ArticleContainer[] = articleContainersOnRail.concat(childContainersForDelete);
    this.detachArticlesOperation = this.undoRedoUtils.createUndoDetachOperationByArticleContainers(
      processedContainers.concat(unremovedComplexPartArticles)
    );
    this.selectionModelService.removeAllSelections();

    this.storeReplacedTextureAreasInfo(processedContainers);
  }

  protected async pushUndoAlternativeReplaceOperation(additionalUndoOperations: IOperation[] = null): Promise<void> {
    if (ArrayUtils.isEmpty(this.insertedArticleUsageIds)) {
      return;
    }
    const attachOperation: UndoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByUsageIds(this.insertedArticleUsageIds);
    if (attachOperation) {
      const replaceOperation: UndoAlternativeReplaceArticleOperation = new UndoAlternativeReplaceArticleOperation(
        attachOperation,
        this.detachArticlesOperation,
        this._articleIdToAlternativeUsageConnectionDatas,
        this.commonMoveArticlesMap,
        this._rotationMap,
        this.insertArticleUtils,
        this.containerService,
        this.labelingArticleRotationHelper,
        this.rotateArticlePresenter,
          this.projectModel
      );
      replaceOperation.additionalOperations = additionalUndoOperations;
      await this.undoRedoHelper.pushUndoOperation(replaceOperation);
    }
  }

  protected getAdditionalUndoOperations(): IOperation[] {
    let additionalUndoOperations: IOperation[] = this._accessoryReplaceEnabled ? this._creationManager.createdUndoOperations : null;
    let updateDirectPrintingOperation: IOperation = null;
    if (!this._articleIdToReplacedArticleId.isEmpty()) {
      updateDirectPrintingOperation = this.directPrintingHelper.updateDirectPrintingOnReplaceAlternativeOperation(
        this._articleIdToReplacedArticleId,
        this.detachArticlesOperation.articleOperations,
        this._replacedTextureAreasInfo
      );
      if (updateDirectPrintingOperation) {
        additionalUndoOperations = additionalUndoOperations
          ? [...additionalUndoOperations, updateDirectPrintingOperation]
          : [updateDirectPrintingOperation];
      }
    }
    return additionalUndoOperations;
  }

  protected async detachArticleContainers(articleContainers: ArticleContainer[]): Promise<void> {
    const usageIdsToDetach: string[] = ArticleContainerUtils.extractArticleGUIDs(articleContainers);
    await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(usageIdsToDetach);
  }

  protected getChildContainersForDelete(childContainers: ArticleContainer[]): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    let wireBridgeUsageIds: string[] = [];
    let wireBridgeContainer: ArticleContainer = null;
    let currentWireBridgeGroupId: string = null;
    let articleUsageIds: string[] = [];
    const deleteGroupedArticleIds: Dictionary<string, string[]> = new Dictionary<string, string[]>();
    for (const childContainer of childContainers) {
      if (ArticleUtils.isWireBridgeArticle(childContainer.article)) {
        if (!childContainer.articleUsage) {
          continue;
        }
        currentWireBridgeGroupId = childContainer.articleUsage.groupId;
        articleUsageIds = deleteGroupedArticleIds.getValue(currentWireBridgeGroupId);
        if (ArrayUtils.isEmpty(articleUsageIds)) {
          articleUsageIds = [childContainer.articleUsage.id];
        } else {
          articleUsageIds.push(childContainer.articleUsage.id);
        }
        deleteGroupedArticleIds.setValue(currentWireBridgeGroupId, articleUsageIds);
        result.push(childContainer);
        const groupedUsageIds: string[] = this.projectArticleService.findArticleUsageIdsByGroupId(currentWireBridgeGroupId);
        wireBridgeUsageIds = this.detachArticleUsageService.collectWireBridgeUsageIdsForRemove(
          childContainer.articleUsage,
          groupedUsageIds.length - articleUsageIds.length
        );
        for (const wireBridgeUsageId of wireBridgeUsageIds) {
          if (articleUsageIds.indexOf(wireBridgeUsageId) > -1) {
            continue;
          }
          wireBridgeContainer = this.containerService.findArticleContainerInGlobal(wireBridgeUsageId);
          if (wireBridgeContainer) {
            result.push(wireBridgeContainer);
          }
        }
      } else {
        result.push(childContainer);
      }
    }
    return result;
  }

  protected async replaceNextWithDelay(): Promise<void> {
    await this.replaceNextArticle();
  }

  protected abstract replaceNextArticle(): Promise<void>;

  protected async processingCreatedArticles(addedArticleGUIDs: string[], moveMap: Dictionary<string, Vector3>): Promise<void> {
    this.selectionModelService.selectArticlesByUsageIds(addedArticleGUIDs, true);

    ArrayUtils.mergeArrays(this.insertedArticleUsageIds, addedArticleGUIDs);
    this.mergeMoveArticlesMap(moveMap, this.processedUsageId);

    await this.replaceNextWithDelay();
  }

  protected async replaceAccessoryArticle(messageType: string): Promise<void> {
    if (this._creationManager) {
      this._creationManager.clean();
      this._creationManager.removeSelection = false;
      this._creationManager.createUndoOperation = false;
      this._creationManager.messageType = messageType;
      await this._creationManager.createArticlesBySuitableConnections(this._suitableGivingConnections, this._acceptingConnectionVectors);
    }
  }

  protected async replaceArticle(articleUsageId: string, messageType: string, detachReplacedArticle: boolean): Promise<InsertedArticleData> {
    const replaceArticleLocation: ReplaceReferenceArticleLocationVO = new ReplaceReferenceArticleLocationVO();
    replaceArticleLocation.articleUsageIdToReplace = articleUsageId;
    replaceArticleLocation.detachReplacedArticle = detachReplacedArticle;
    if (this.article instanceof CustomArticleVO) {
      this.processArticleLocationByCustomArticle(this.article, replaceArticleLocation);
    }
    return this._insertArticleService.insertArticleByArticleLocation(this.article, replaceArticleLocation, messageType);
  }

  protected processArticleLocationByCustomArticle(customArticle: CustomArticleVO, articleLocation: ArticleLocationVO): void {
    const articleUsageToCreate: ArticleUsageVO = this.projectArticleService.createNewArticleUsage(customArticle.identity, customArticle.version);
    CustomArticleUtils.applyCustomArticleInfoToArticleUsage(articleUsageToCreate, customArticle.customArticleInfo);
    articleLocation.existingArticleUsageId = articleUsageToCreate.id;
    articleLocation.checkConnectionProperties = false;
  }

  protected commonFilterArticleContainersOnRail(articleUsageIds: string[]): ArticleContainer[] {
    if (ArrayUtils.isEmpty(articleUsageIds)) {
      return [];
    }

    const articleContainersOnRail: ArticleContainer[] = [];
    for (const usageId of articleUsageIds) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
      if (this.articleConnectionService.isArticleConnectedToRangeOnLineArticleWithConfigured(articleContainer)) {
        if (!IsProjectType.mcsGds(this.projectModel.currentProject) && this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer)) {
          const baseCompositePartArticles: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(articleContainer);
          if (baseCompositePartArticles.length > 1) {
            continue;
          }
        }
        articleContainersOnRail.push(articleContainer);
      }
    }
    return ArticleContainerUtils.sortArticleContainersByDistanceOfPosition(articleContainersOnRail, new Vector3());
  }

  private filterComplexArticlesOnRail(articleContainersOnRail: ArticleContainer[]): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    const processedCompositeUsageIdToBaseContainers: Dictionary<string, ArticleContainer[]> = new Dictionary<string, ArticleContainer[]>();
    let currentCompositeUsageId: string;
    let baseContainers: ArticleContainer[];
    for (const container of articleContainersOnRail) {
      currentCompositeUsageId = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(container.articleUsage);
      if (!currentCompositeUsageId || this.projectHelper.isCompositePartArticleModificationAllowed(container)) {
        result.push(container);
        continue;
      }

      baseContainers = processedCompositeUsageIdToBaseContainers.getValue(currentCompositeUsageId);
      if (ArrayUtils.isEmpty(baseContainers)) {
        baseContainers = [];
      }
      baseContainers.push(container);
      processedCompositeUsageIdToBaseContainers.setValue(currentCompositeUsageId, baseContainers);
    }

    for (const key of processedCompositeUsageIdToBaseContainers.keys()) {
      baseContainers = ArticleContainerUtils.sortArticleContainersByDistanceOfPosition(processedCompositeUsageIdToBaseContainers.getValue(key), new Vector3(0, 0, 0));
      if (baseContainers.length > 0) {
        result.push(baseContainers[0]);
      }
    }

    return result;
  }

  private filterComplexChildArticleContainers(articlesOnRail: ArticleContainer[], childArticleContainers: ArticleContainer[]): ArticleContainer[] {
    if (ArrayUtils.isEmpty(childArticleContainers)) {
      return childArticleContainers;
    }

    const processedCompositeUsageIdsOnRail: string[] = this.extractCompositeUsageIdsFromArticleContainers(articlesOnRail);
    if (ArrayUtils.isEmpty(processedCompositeUsageIdsOnRail)) {
      return childArticleContainers;
    }

    const result: ArticleContainer[] = [];
    let currentCompositeUsageId: string;
    for (const childContainer of childArticleContainers) {
      currentCompositeUsageId = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(childContainer.articleUsage);

      if (!currentCompositeUsageId || this.projectHelper.isCompositePartArticleModificationAllowed(childContainer)) {
        result.push(childContainer);
      } else {
        if (processedCompositeUsageIdsOnRail.indexOf(currentCompositeUsageId) === -1) {
          result.push(childContainer);
        }
      }
    }

    return result;
  }

  private extractCompositeUsageIdsFromArticleContainers(articlesOnRail: ArticleContainer[]): string[] {
    const result: string[] = [];
    let currentCompositeUsageId: string;
    for (const container of articlesOnRail) {
      currentCompositeUsageId = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(container.articleUsage);
      if (!!currentCompositeUsageId && result.indexOf(currentCompositeUsageId) < 0) {
        result.push(currentCompositeUsageId);
      }
    }

    return result;
  }

  protected revertArticleDetachOperation(articleUsageId: string): void {
    if (this.detachArticlesOperation) {
      this.detachArticlesOperation.removeArticleOperationByUsageId(articleUsageId);
    }
    this._articleIdToReplacedArticleId.remove(articleUsageId);
  }

  protected revertArticleDetachOperations(articleContainers: ArticleContainer[]): void {
    if (ArrayUtils.isEmpty(articleContainers)) {
      return;
    }
    for (const articleContainer of articleContainers) {
      this.revertArticleDetachOperation(articleContainer.articleUsage.id);
    }
  }

  protected removeContainersFromArticlesToDetach(articleContainers: ArticleContainer[]): void {
    this.revertArticleDetachOperations(articleContainers);
  }

  private selectArticleByProcessedId(usageId: string): void {
    if (!this.processedUsageId) {
      return;
    }

    if (this.selectedArticleIds.indexOf(this.processedUsageId) >= 0) {
      this.selectArticle(usageId);
    }
  }

  private selectArticle(usageId: string): void {
    if (!this._selectReplacedArticle) {
      return;
    }
    const articleToSelect: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
    if (!articleToSelect) {
      return;
    }
    const articleUsage: ArticleUsageVO = articleToSelect.articleUsage;
    const selectionGroupIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(articleUsage);
    this.selectionModelService.selectObject(selectionGroupIdentity, true, false);
  }

  protected async finalizeFailureProcessingArticle(): Promise<void> {
    this.revertArticleDetachOperation(this.processedUsageId);
    this.selectArticleByProcessedId(this.processedUsageId);
  }

  protected mergeMoveArticlesMap(sourceMoveMap: Dictionary<string, Vector3>, articleGUID: string = null): void {
    if (!this.commonMoveArticlesMap) {
      this.commonMoveArticlesMap = new Dictionary<string, Vector3>();
    }
    if (articleGUID && this.commonMoveArticlesMap.containsKey(articleGUID)) {
      this.commonMoveArticlesMap.remove(articleGUID);
    }
    InsertArticleUtils.mergeMoveMap(this.commonMoveArticlesMap, sourceMoveMap);
  }

  protected async reconnectAccessoryArticle(accessoryContainer: ArticleContainer,
                                      replacedContainer: ArticleContainer,
                                      acceptingConnections: Connection[]): Promise<AlternativeUsageConnectionData[]> {
    const alternativeUsageConnectionDatas: AlternativeUsageConnectionData[] = [];
    const articleGivingAreaIds: MetaElementUsageIdentityVO[] = accessoryContainer.getCurrentArticleGivingAreaIds();
    const givingConnections: Connection[] = this.getGivingConnectionsConnectedToProcessedContainer(
      articleGivingAreaIds,
      replacedContainer.articleGUID,
      accessoryContainer
    );

    let alternativeUsageConnectionData: AlternativeUsageConnectionData;
    const checkExactPosition: boolean = articleGivingAreaIds.length > 1;
    const isLabelingArticle: boolean = ArticleUtils.isLabelingArticle(accessoryContainer.article);
    for (const givingConnectionConnection of givingConnections) {
      const parentAcceptingConnection: Connection = this.getRelatedAcceptingConnection(replacedContainer, givingConnectionConnection);
      const nearestAcceptingConnection: Connection = this.getNearestConnectionInSource(
        parentAcceptingConnection,
        acceptingConnections,
        checkExactPosition
      );
      if (
        !nearestAcceptingConnection ||
        (isLabelingArticle && !this.isEqualLabelingConnections(parentAcceptingConnection, nearestAcceptingConnection, accessoryContainer))
      ) {
        return [];
      }

      const acceptingProperties: PropertyVO[] = ConnectionInfoUtils.createRequirementPropertyList(
        nearestAcceptingConnection.metaElementVO as ConnectionAreaVO,
        nearestAcceptingConnection.articleContainer.article
      );
      const givingProperties: PropertyVO[] = ConnectionInfoUtils.createFeaturePropertyList(
        givingConnectionConnection.metaElementVO as ConnectionAreaVO,
        givingConnectionConnection.articleContainer.article
      );
      const identityForAccessoryCheck: number = nearestAcceptingConnection.articleContainer.article.identity;
      const compositeIdentityForAccesoryCheck: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
        nearestAcceptingConnection.articleContainer.articleUsage,
        ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID
      );
      if (
        PropertyUtils.checkProperties(givingProperties, acceptingProperties) &&
        (this.articleAccessoryManager.isAccessoryArticleExists(identityForAccessoryCheck, accessoryContainer.article.identity) ||
          this.articleAccessoryManager.isAccessoryArticleExists(compositeIdentityForAccesoryCheck, accessoryContainer.article.identity))
      ) {
        alternativeUsageConnectionData = new AlternativeUsageConnectionData();
        alternativeUsageConnectionData.articleUsageId = accessoryContainer.articleGUID;
        alternativeUsageConnectionData.givingConnectionName = givingConnectionConnection.connectionName;
        alternativeUsageConnectionData.oldParentUsageId = replacedContainer.articleGUID;
        alternativeUsageConnectionData.oldParentConnectionName = parentAcceptingConnection.connectionName;
        alternativeUsageConnectionData.newParentConnectionName = nearestAcceptingConnection.connectionName;
        alternativeUsageConnectionData.newParentUsageId = nearestAcceptingConnection.articleContainer.articleGUID;
        alternativeUsageConnectionDatas.push(alternativeUsageConnectionData);

        await this.connectArticleByConnections(givingConnectionConnection, nearestAcceptingConnection, replacedContainer.articleGUID,
          !checkExactPosition);
      } else {
        return [];
      }
    }
    return alternativeUsageConnectionDatas;
  }

  private getGivingConnectionsConnectedToProcessedContainer(
    articleGivingAreaIds: MetaElementUsageIdentityVO[],
    replacedUsageId,
    accessoryContainer: ArticleContainer
  ): Connection[] {
    const result: Connection[] = [];
    let givingConnection: Connection;
    for (const identity of articleGivingAreaIds) {
      givingConnection = accessoryContainer.findFirstConnectionByName(identity.metaElementName);
      if (!givingConnection) {
        return [];
      }

      const metaElementUsage: ConnectionAreaUsageVO = givingConnection.metaElementUsage as ConnectionAreaUsageVO;
      if (!metaElementUsage || metaElementUsage.connection.acceptingAreaId.articleUsageId !== replacedUsageId) {
        continue;
      }

      result.push(givingConnection);
    }

    return result;
  }

  private getRelatedAcceptingConnection(parentContainer: ArticleContainer, givingConnection: Connection): Connection {
    const parentConnectionName: string = (givingConnection.metaElementUsage as ConnectionAreaUsageVO).connection.acceptingAreaId.metaElementName;
    return parentContainer.findFirstConnectionByName(parentConnectionName);
  }

  protected getNearestConnectionInSource(targetConnection: Connection, sourceConnections: Connection[], checkExactPosition: boolean): Connection {
    const targetMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(targetConnection.globalRectangle);
    let nearestCube: Connection;
    let nearestDistanceToConnection: number = Number.MAX_VALUE;
    let cubesDistance: number;
    let sourceMiddlePoint: Vector3;
    for (const connection of sourceConnections) {
      sourceMiddlePoint = MathUtils.calculateMiddlePointByRectangle(connection.globalRectangle);
      cubesDistance = targetMiddlePoint.distanceTo(sourceMiddlePoint);
      if (checkExactPosition && MathUtils.compare(cubesDistance, 0) !== 0) {
        continue;
      }

      if (cubesDistance < nearestDistanceToConnection) {
        nearestDistanceToConnection = cubesDistance;
        nearestCube = connection;
      }
    }
    return nearestCube;
  }

  protected isEqualLabelingConnections(firstConnection: Connection, secondConnection: Connection, labelingArticle: ArticleContainer = null): boolean {
    return (
      this.isEqualLabelingRectangles(firstConnection, secondConnection) &&
      this.isEqualSpacingDatas(firstConnection, secondConnection, labelingArticle)
    );
  }

  private isEqualLabelingRectangles(firstConnection: Connection, secondConnection: Connection): boolean {
    const firstRectangle: Rectangle3D = this.labelingUtils.calculateLabelingRectangle(firstConnection);
    const secondRectangle: Rectangle3D = this.labelingUtils.calculateLabelingRectangle(secondConnection);
    if (!firstRectangle || !secondRectangle) {
      return false;
    }
    return MathUtils.equalsRectangle3D(firstRectangle, secondRectangle);
  }

  private isEqualSpacingDatas(firstConnection: Connection, secondConnection: Connection, labelingArticle: ArticleContainer): boolean {
    const firstSpacingData: TextCheckSpacingData = this.getSpacingData(firstConnection, labelingArticle);
    const secondSpacingData: TextCheckSpacingData = this.getSpacingData(secondConnection, labelingArticle);
    if (!firstSpacingData && !secondSpacingData) {
      return true;
    }
    return firstSpacingData && firstSpacingData.equals(secondSpacingData);
  }

  private getSpacingData(targetConnection: Connection, labelingArticle: ArticleContainer): TextCheckSpacingData {
    if (labelingArticle && ArticleUtils.isDynamicArticle(labelingArticle.article)) {
      if (this.labelingUtils.isArticleConnectedToAdapters(labelingArticle)) {
        return this.labelingUtils.getAdapterLabelStripTextCheckSpacingData(targetConnection);
      } else {
        return this.labelingUtils.getDefaultLabelStripTextCheckSpacingData(targetConnection);
      }
    } else {
      return this.labelingUtils.getTextCheckSpacingDataByConnection(targetConnection);
    }
  }

  private async connectArticleByConnections(givingConnection: Connection, acceptingConnection: Connection, replacedArticleGuid: string,
                                      reconnectArticle: boolean): Promise<void> {
    this.projectMetaElementService.createUsageBetweenConnections(givingConnection, acceptingConnection);
    const givingArea: ConnectionAreaUsageVO = givingConnection.metaElementUsage as ConnectionAreaUsageVO;
    givingArea.connection = CloneUtils.cloneConnectionVO(givingArea.connection);
    if (reconnectArticle) {
      const accessoryContainer: ArticleContainer = givingConnection.articleContainer;
      await this.reconnectArticleWithChildren(accessoryContainer, acceptingConnection, givingConnection, replacedArticleGuid);
    }
  }

  private async reconnectArticleWithChildren(containerToReconnect: ArticleContainer, acceptingConnection: Connection,
                                       givingConnection: Connection, replacedArticleGuid: string): Promise<void> {
    const acceptingRectangle: Rectangle3D = acceptingConnection.globalRectangle;
    const givingRectangle: Rectangle3D = givingConnection.globalRectangle.clone();

    let rotMatrix: Matrix4;
    if (!ArticleUtils.isWireBridgeArticle(givingConnection.articleContainer.article)) {
      rotMatrix = MathUtils.getRotationMatrix(givingRectangle.toFace(), acceptingRectangle.toFace());
      this._rotationMap.setValue(containerToReconnect.articleGUID, rotMatrix);
    }
    const givingMiddlePoint: Vector3 = ConnectArticleHelper.calculateGivingMiddlePoint(acceptingRectangle, givingRectangle, rotMatrix);

    ConnectArticleHelper.connectContainer(containerToReconnect, rotMatrix, givingMiddlePoint);
    if (ArticleUtils.isLabelingArticle(containerToReconnect.article)) {
      this.labelingArticleRotationHelper.rotateLabelingArticle(this.rotateArticlePresenter, containerToReconnect);
    }

    const childContainers: ArticleContainer[] = this.insertArticleUtils.findChildArticleContainers([containerToReconnect]);

    // check if giving rectangle changed after connect article
    const modifiedRectangle: Rectangle3D = givingConnection.globalRectangle.clone();
    const givingChanged: boolean = !MathUtils.equalsRectangle3D(givingRectangle, modifiedRectangle);
    await this.connectChildArticles(childContainers, rotMatrix, givingMiddlePoint, givingChanged);

    const appliedRotationAngle: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
      containerToReconnect.articleUsage,
      ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
    );
    if (!isNaN(appliedRotationAngle)) {
      this.rotateArticlePresenter.rotateArticleContainer(containerToReconnect, appliedRotationAngle, false, true, true, true, false);
    }

    let moveOffset: Vector3 = givingMiddlePoint;
    const parentOffset: Vector3 = this.commonMoveArticlesMap.getValue(replacedArticleGuid);
    if (parentOffset) {
      const rotatedParentOffset: Vector3 = !rotMatrix ? parentOffset : MathUtils.rotate([parentOffset], rotMatrix)[0];
      moveOffset = moveOffset.add(rotatedParentOffset);
    }

    const moveMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    moveMap.setValue(containerToReconnect.articleGUID, moveOffset);

    this.mergeMoveArticlesMap(moveMap, null);
  }

  private async connectChildArticles(articleContainers: ArticleContainer[], rotMatrix: Matrix4, givingMiddlePoint: Vector3,
                               parentPositionModified: boolean): Promise<void> {
    let notReconnectedContainers: ArticleContainer[] = [];
    for (const container of articleContainers) {
      if (notReconnectedContainers.indexOf(container) > -1) {
        continue;
      }

      const articleGivingAreaIds: MetaElementUsageIdentityVO[] = container.getCurrentArticleGivingAreaIds();
      if (articleGivingAreaIds.length > 1 && parentPositionModified) {
        // in case when all givings connect to one parent then article whatever will be deleted
        const childContainers: ArticleContainer[] = this.insertArticleUtils.findChildArticleContainers([container]);
        const containersToDetach: ArticleContainer[] = childContainers.concat([container]);
        notReconnectedContainers = notReconnectedContainers.concat(containersToDetach);
        continue;
      }

      ConnectArticleHelper.connectContainer(container, rotMatrix, givingMiddlePoint);
      if (ArticleUtils.isLabelingArticle(container.article)) {
        this.labelingArticleRotationHelper.rotateLabelingArticle(this.rotateArticlePresenter, container);
      }
    }

    const replacedArticles: ArticleContainer[] = articleContainers.concat();
    ArrayUtils.removeAllItemsInArray(replacedArticles, notReconnectedContainers);

    for (const articleContainer of replacedArticles) {
      this.revertArticleDetachOperation(articleContainer.articleGUID);
      this._rotationMap.setValue(articleContainer.articleGUID, rotMatrix);
    }

    await this.detachArticleContainers(notReconnectedContainers);
  }

  protected storeReplacedTextureAreasInfo(processedContainers: ArticleContainer[] = []): void {
    if (ArrayUtils.isNotEmpty(processedContainers)) {
      processedContainers.forEach((c) => {
        const textureAreas: Connection[] = c.connections.filter((c) => c.metaElementUsage instanceof TextureAreaUsageVO);
        const textureAreaInfos: TextureAreaInfo[] = [];
        textureAreas.forEach((ta) => {
          const taUsage: TextureAreaUsageVO = ta.metaElementUsage as TextureAreaUsageVO;
          textureAreaInfos.push({
            compositionRectangle: ta.compositionRectangle,
            textValue: taUsage.textValue,
            imageUrl: taUsage.imageUrl,
            imageHash: ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
              c.articleUsage,
              ta.connectionName,
              DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
            ),
            previousMerged: !!taUsage.previousMergedUsageId,
            nextMerged: !!taUsage.nextMergedUsageId,
          });
        });
        this._replacedTextureAreasInfo.setValue(c.articleGUID, textureAreaInfos);
      });
    }
  }

  protected clear(): void {
    this.isActive = false;
    this._selectReplacedArticle = true;

    this.article = null;
    this.processedUsageId = null;
    this.selectedArticleIds = [];

    this.insertedArticleUsageIds = [];
    this.commonMoveArticlesMap = new Dictionary<string, Vector3>();
    this.detachArticlesOperation = null;

    this._rotationMap = new Dictionary<string, Matrix4>();
    this._articleIdToAlternativeUsageConnectionDatas = new Dictionary<string, AlternativeUsageConnectionData[]>();

    if (this._creationManager) {
      this._creationManager.clean();
      this._creationManager = null;
    }

    this._accessoryReplaceEnabled = false;
    this._suitableGivingConnections = null;
    this._acceptingConnectionVectors = [];

    this._articleIdToReplacedArticleId = new Dictionary<string, string>();
    this._replacedTextureAreasInfo = new Dictionary<string, TextureAreaInfo[]>();
  }
}
